import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type Customer = {
  id: string;
  business_name: string;
  legal_name: string | null;
  rfc: string | null;
  regimen_fiscal: string | null;
  cfdi_use: string | null;
  forma_pago_default: string | null;
  metodo_pago_default: string | null;
  customer_type: string;
  status: string;
  primary_email: string | null;
  primary_phone: string | null;
  whatsapp: string | null;
  tier_id: string | null;
  tier_code: string | null;
  tier_name: string | null;
  tier_discount_pct: number | null;
  default_markup_pct: number | null;
  lutron_lines_certified: string[] | null;
  total_orders: number | null;
  total_revenue_mxn: number | null;
  credit_limit_mxn: number | null;
  credit_balance_mxn: number | null;
  credit_days: number | null;
  city: string | null;
  state: string | null;
  line1: string | null;
  neighborhood: string | null;
  postal_code: string | null;
  created_at: string;
};

type Tier = {
  id: string;
  code: string;
  name: string;
  default_discount_pct: number;
  sort_order: number;
};

type Tab = 'all' | 'lead' | 'active' | 'inactive';
type TierGroup = 'all' | 'distribuidor' | 'minorista' | 'electricista' | 'integrador' | 'otro';

export function AdminClientes() {
  const [items, setItems] = useState<Customer[]>([]);
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [tierFilter, setTierFilter] = useState<TierGroup>('all');
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [discountsByCustomer, setDiscountsByCustomer] = useState<Record<string, { nuled?: number; lutron?: number }>>({});

  useEffect(() => {
    loadCustomers();
    loadTiers();
  }, []);

  async function loadCustomers() {
    setLoading(true);
    const { data, error } = await supabase.from('customers_admin')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) console.error(error);
    setItems((data as Customer[]) ?? []);
    setLoading(false);
    // Cargar descuentos por marca para todos
    const { data: cdb } = await supabase.from('customer_discount_brand')
      .select('customer_id, brand_code, discount_pct');
    const map: Record<string, { nuled?: number; lutron?: number }> = {};
    for (const r of ((cdb as any[]) ?? [])) {
      if (!map[r.customer_id]) map[r.customer_id] = {};
      (map[r.customer_id] as any)[r.brand_code] = Number(r.discount_pct);
    }
    setDiscountsByCustomer(map);
  }

  async function updateBrandDiscount(customerId: string, brand: 'nuled' | 'lutron', value: number) {
    // Optimistic UI
    setDiscountsByCustomer(prev => ({
      ...prev,
      [customerId]: { ...prev[customerId], [brand]: value },
    }));
    await supabase.from('customer_discount_brand').upsert({
      customer_id: customerId,
      brand_code: brand,
      discount_pct: value,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'customer_id,brand_code' });
  }

  async function loadTiers() {
    const { data } = await supabase.from('customer_tiers')
      .select('id, code, name, default_discount_pct, sort_order')
      .eq('active', true)
      .order('sort_order');
    setTiers((data as Tier[]) ?? []);
  }

  // Agrupa tier code en categoría para filtros (legacy + nuevos)
  function tierGroup(code: string | null): TierGroup {
    if (!code) return 'otro';
    if (code.startsWith('d') || code.includes('distribuidor') || code === 'mayorista_55') return 'distribuidor';
    if (code.includes('minorista')) return 'minorista';
    if (code === 'electricista') return 'electricista';
    if (code === 'integrador') return 'integrador';
    return 'otro';
  }

  const counts = useMemo(() => ({
    all: items.length,
    lead: items.filter(c => c.status === 'lead').length,
    active: items.filter(c => c.status === 'active').length,
    inactive: items.filter(c => c.status === 'inactive' || c.status === 'rejected').length,
  }), [items]);

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return items.filter(c => {
      if (tab === 'lead' && c.status !== 'lead') return false;
      if (tab === 'active' && c.status !== 'active') return false;
      if (tab === 'inactive' && !['inactive', 'rejected'].includes(c.status)) return false;
      if (tierFilter !== 'all' && tierGroup(c.tier_code) !== tierFilter) return false;
      if (qn) {
        return (
          c.business_name.toLowerCase().includes(qn) ||
          (c.legal_name?.toLowerCase().includes(qn) ?? false) ||
          (c.rfc?.toLowerCase().includes(qn) ?? false) ||
          (c.primary_email?.toLowerCase().includes(qn) ?? false) ||
          (c.primary_phone?.toLowerCase().includes(qn) ?? false)
        );
      }
      return true;
    });
  }, [items, q, tab, tierFilter]);

  return (
    <AdminContainer
      title="Clientes"
      eyebrow={`Distribuidores · ${items.length} totales`}
      action={
        <button style={btnPrimary} onClick={() => setShowNewForm(true)}>
          + Alta de distribuidor
        </button>
      }
    >
      {/* Tabs por status */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: `1px solid ${theme.border}` }}>
        <TabBtn active={tab === 'all'} onClick={() => setTab('all')}>
          Todos <span style={{ opacity: 0.6 }}>({counts.all})</span>
        </TabBtn>
        <TabBtn active={tab === 'lead'} onClick={() => setTab('lead')}>
          Aplicaciones <span style={{ opacity: 0.6 }}>({counts.lead})</span>
        </TabBtn>
        <TabBtn active={tab === 'active'} onClick={() => setTab('active')}>
          Activos <span style={{ opacity: 0.6 }}>({counts.active})</span>
        </TabBtn>
        <TabBtn active={tab === 'inactive'} onClick={() => setTab('inactive')}>
          Inactivos <span style={{ opacity: 0.6 }}>({counts.inactive})</span>
        </TabBtn>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          placeholder="Buscar nombre, RFC, email, teléfono…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{
            flex: 1,
            minWidth: 260,
            padding: '10px 14px',
            border: `1px solid ${theme.border}`,
            fontSize: 14,
            outline: 'none',
            color: theme.text,
            fontFamily: theme.font.sans,
            background: theme.bg,
          }}
        />
        <Pill active={tierFilter === 'all'} onClick={() => setTierFilter('all')}>Todos los tipos</Pill>
        <Pill active={tierFilter === 'distribuidor'} onClick={() => setTierFilter('distribuidor')}>Distribuidor</Pill>
        <Pill active={tierFilter === 'minorista'} onClick={() => setTierFilter('minorista')}>Minorista</Pill>
        <Pill active={tierFilter === 'electricista'} onClick={() => setTierFilter('electricista')}>Electricista</Pill>
        <Pill active={tierFilter === 'integrador'} onClick={() => setTierFilter('integrador')}>Integrador</Pill>
      </div>

      <div style={{ marginBottom: 8, fontSize: 12, color: theme.textMuted }}>
        {loading ? 'Cargando…' : `${filtered.length} de ${items.length}`}
      </div>

      {/* Tabla */}
      <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 1000 }}>
          <thead>
            <tr style={{ background: theme.bgSoft }}>
              <Th>Empresa</Th>
              <Th>RFC</Th>
              <Th>Contacto</Th>
              <Th>Tier</Th>
              <Th style={{ textAlign: 'center', minWidth: 90 }}>
                Desc NULED
              </Th>
              <Th style={{ textAlign: 'center', minWidth: 90 }}>
                Desc Lutron
              </Th>
              <Th>Lutron certs</Th>
              <Th style={{ textAlign: 'right' }}>Revenue MXN</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>Acción</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map(c => (
              <tr key={c.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                <Td>
                  <div style={{ fontWeight: 600, color: theme.brand }}>{c.business_name}</div>
                  {c.legal_name && c.legal_name !== c.business_name && (
                    <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>{c.legal_name}</div>
                  )}
                  <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {c.customer_type}
                  </div>
                </Td>
                <Td>
                  <span style={{ fontFamily: theme.font.mono, fontSize: 11, color: theme.textMuted }}>
                    {c.rfc ?? '—'}
                  </span>
                </Td>
                <Td>
                  {c.primary_email && (
                    <div style={{ fontSize: 12, wordBreak: 'break-word' }}>{c.primary_email}</div>
                  )}
                  {c.primary_phone && (
                    <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2, fontFamily: theme.font.mono }}>
                      {c.primary_phone}
                    </div>
                  )}
                  {!c.primary_email && !c.primary_phone && '—'}
                </Td>
                <Td>
                  {c.tier_code ? (
                    <Tag color={
                      c.tier_code.startsWith('d1') || c.tier_code === 'mayorista_55' ? theme.accentDark
                      : c.tier_code.startsWith('d2') || c.tier_code === 'distribuidor_35' ? '#9A7B00'
                      : theme.textMuted
                    }>
                      {c.tier_code.toUpperCase()}
                    </Tag>
                  ) : '—'}
                </Td>
                <Td style={{ textAlign: 'center' }}>
                  <DiscountCell
                    value={discountsByCustomer[c.id]?.nuled ?? 0}
                    onSave={(v) => updateBrandDiscount(c.id, 'nuled', v)}
                  />
                </Td>
                <Td style={{ textAlign: 'center' }}>
                  <DiscountCell
                    value={discountsByCustomer[c.id]?.lutron ?? 0}
                    onSave={(v) => updateBrandDiscount(c.id, 'lutron', v)}
                  />
                </Td>
                <Td>
                  {c.lutron_lines_certified && c.lutron_lines_certified.length > 0 ? (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      {c.lutron_lines_certified.slice(0, 3).map(line => (
                        <span key={line} style={{
                          fontSize: 9,
                          fontWeight: 700,
                          padding: '2px 5px',
                          background: theme.brandTag.lutron + '22',
                          color: theme.brandTag.lutron,
                          textTransform: 'uppercase',
                        }}>{line}</span>
                      ))}
                      {c.lutron_lines_certified.length > 3 && (
                        <span style={{ fontSize: 10, color: theme.textMuted }}>
                          +{c.lutron_lines_certified.length - 3}
                        </span>
                      )}
                    </div>
                  ) : '—'}
                </Td>
                <Td style={{ textAlign: 'right' }}>
                  {c.total_revenue_mxn ? (
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: theme.brand }}>
                        ${Number(c.total_revenue_mxn).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
                      </div>
                      <div style={{ fontSize: 10, color: theme.textMuted }}>
                        {c.total_orders ?? 0} {c.total_orders === 1 ? 'orden' : 'órdenes'}
                      </div>
                    </div>
                  ) : '—'}
                </Td>
                <Td>
                  <Tag color={
                    c.status === 'active' ? theme.accentDark
                    : c.status === 'lead' ? '#9A7B00'
                    : c.status === 'rejected' ? '#B91C1C'
                    : theme.textMuted
                  }>
                    {c.status}
                  </Tag>
                </Td>
                <Td style={{ textAlign: 'right' }}>
                  {c.status === 'lead' ? (
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => approveCustomer(c.id, loadCustomers)}
                        style={btnSmall(theme.accent, theme.brand)}
                      >
                        Aprobar
                      </button>
                      <button
                        onClick={() => rejectCustomer(c.id, loadCustomers)}
                        style={btnSmall('transparent', '#B91C1C', '#B91C1C')}
                      >
                        Rechazar
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingId(c.id)}
                      style={btnSmall('transparent', theme.brand, theme.border)}
                    >
                      Editar
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>
            No hay clientes que coincidan con el filtro.
          </div>
        )}
      </div>

      {filtered.length > 200 && (
        <div style={{ marginTop: 12, fontSize: 12, color: theme.textMuted, textAlign: 'center' }}>
          Mostrando 200 de {filtered.length}. Refina con búsqueda o filtros.
        </div>
      )}

      {showNewForm && (
        <CustomerModal
          mode="create"
          tiers={tiers}
          onClose={() => setShowNewForm(false)}
          onSaved={() => { setShowNewForm(false); loadCustomers(); }}
        />
      )}
      {editingId && (
        <CustomerModal
          mode="edit"
          customerId={editingId}
          tiers={tiers}
          onClose={() => setEditingId(null)}
          onSaved={() => { setEditingId(null); loadCustomers(); }}
        />
      )}
    </AdminContainer>
  );
}

function DiscountCell({ value, onSave }: { value: number; onSave: (v: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value.toString());
  useEffect(() => { setDraft(value.toString()); }, [value]);

  const commit = () => {
    const n = parseFloat(draft);
    if (!isNaN(n) && n >= 0 && n <= 90 && n !== value) onSave(n);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        type="number"
        min={0}
        max={90}
        step={0.5}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setDraft(value.toString()); setEditing(false); }
        }}
        autoFocus
        style={{
          width: 60, padding: '4px 6px', textAlign: 'center',
          border: `1px solid ${theme.accent}`, borderRadius: 0,
          fontSize: 12, fontFamily: theme.font.sans, fontWeight: 700,
          outline: 'none', background: theme.bg, color: theme.brand,
        }}
      />
    );
  }
  const bg = value === 0 ? 'transparent'
    : value >= 50 ? '#FEF3C7'
    : value >= 30 ? '#DBEAFE'
    : '#F3F4F6';
  return (
    <button
      onClick={() => setEditing(true)}
      title="Click para editar"
      style={{
        background: bg, border: 'none',
        padding: '4px 8px', fontSize: 12, fontWeight: 700,
        color: value === 0 ? theme.textMuted : theme.brand,
        cursor: 'pointer', minWidth: 56,
        fontFamily: theme.font.sans,
      }}
    >
      {value}%
    </button>
  );
}

async function approveCustomer(id: string, reload: () => void) {
  if (!confirm('¿Aprobar este distribuidor? Quedará activo y podrá ver precios mayoreo.')) return;
  const { error } = await supabase.from('customers')
    .update({ status: 'active' })
    .eq('id', id);
  if (error) { alert(`Error: ${error.message}`); return; }
  reload();
}

async function rejectCustomer(id: string, reload: () => void) {
  if (!confirm('¿Rechazar este distribuidor?')) return;
  const { error } = await supabase.from('customers')
    .update({ status: 'rejected' })
    .eq('id', id);
  if (error) { alert(`Error: ${error.message}`); return; }
  reload();
}

function CustomerModal({ mode, customerId, tiers, onClose, onSaved }: {
  mode: 'create' | 'edit';
  customerId?: string;
  tiers: Tier[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [businessName, setBusinessName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [rfc, setRfc] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [line1, setLine1] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [regimenFiscal, setRegimenFiscal] = useState('');
  const [cfdiUse, setCfdiUse] = useState('');
  const [formaPago, setFormaPago] = useState('');
  const [metodoPago, setMetodoPago] = useState('');
  const [tierId, setTierId] = useState<string | null>(null);
  const [type, setType] = useState('distributor');
  const [status, setStatus] = useState('active');
  const [creditMxn, setCreditMxn] = useState(0);
  const [creditDays, setCreditDays] = useState(0);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // === CSF upload (en create Y edit — siempre puedes actualizar fiscal data) ===
  const [csfParsing, setCsfParsing] = useState(false);
  const [csfErr, setCsfErr] = useState<string | null>(null);
  const [csfOk, setCsfOk] = useState<string | null>(null);

  async function handleCsfUpload(file: File) {
    setCsfErr(null);
    setCsfOk(null);
    if (file.type !== 'application/pdf') {
      setCsfErr('El archivo debe ser un PDF de la CSF del SAT.');
      return;
    }
    if (file.size > 7 * 1024 * 1024) {
      setCsfErr('PDF demasiado grande (max 7 MB).');
      return;
    }
    setCsfParsing(true);
    try {
      // Convertir a base64
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let bin = '';
      const chunk = 0x8000;
      for (let i = 0; i < bytes.length; i += chunk) {
        bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      const b64 = btoa(bin);

      const { data, error } = await supabase.functions.invoke('parse-csf', {
        body: { pdf_base64: b64 },
      });
      if (error) {
        setCsfParsing(false);
        // Extraer mensaje real del context
        try {
          const ctx = (error as any).context;
          if (ctx?.json) {
            const body = await ctx.json();
            setCsfErr(body.error || error.message);
            return;
          }
        } catch { /* ignore */ }
        setCsfErr(error.message);
        return;
      }
      const r = data as { ok?: boolean; data?: any; error?: string };
      if (!r?.ok || !r.data) {
        setCsfParsing(false);
        setCsfErr(r?.error || 'No se pudo extraer información del PDF');
        return;
      }

      const d = r.data;
      // CSF es la fuente de verdad fiscal del SAT → sobreescribir siempre que venga dato.
      // El usuario subió CSF intencionalmente, sabe que está actualizando datos fiscales.
      if (d.business_name) setBusinessName(d.business_name);
      if (d.legal_name) setLegalName(d.legal_name);
      if (d.rfc) setRfc(d.rfc.toUpperCase());
      if (d.regimen_fiscal) setRegimenFiscal(d.regimen_fiscal);
      if (d.primary_email && !email) setEmail(d.primary_email); // email no viene de CSF normalmente
      if (d.primary_phone && !phone) setPhone(d.primary_phone);

      const addr = d.address || {};
      if (addr.line1) setLine1(addr.line1);
      if (addr.neighborhood) setNeighborhood(addr.neighborhood);
      if (addr.city) setCity(addr.city);
      if (addr.state) setState(addr.state);
      if (addr.postal_code) setPostalCode(addr.postal_code);

      setCsfParsing(false);
      setCsfOk(`✓ Datos del SAT extraídos${d.rfc ? ` (RFC ${d.rfc})` : ''}. Revisa y ${mode === 'edit' ? 'Guarda cambios' : 'dale Crear'}.`);
    } catch (e) {
      setCsfParsing(false);
      setCsfErr((e as Error).message || 'Error procesando el PDF');
    }
  }

  useEffect(() => {
    if (mode !== 'edit' || !customerId) return;
    (async () => {
      // Leer directo de customers (no de la view, porque la view NO incluye
      // regimen_fiscal / cfdi_use / forma_pago_default / metodo_pago_default
      // — esto causaba que al guardar se persistiera null y se perdían).
      const { data: c } = await supabase
        .from('customers')
        .select('business_name, legal_name, rfc, regimen_fiscal, cfdi_use, forma_pago_default, metodo_pago_default, primary_email, primary_phone, whatsapp, tier_id, customer_type, status, credit_limit_mxn, credit_days')
        .eq('id', customerId)
        .maybeSingle();

      // Dirección default en paralelo
      const { data: addr } = await supabase
        .from('customer_addresses')
        .select('line1, neighborhood, city, state, postal_code')
        .eq('customer_id', customerId)
        .eq('kind', 'billing')
        .order('is_default', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (c) {
        setBusinessName(c.business_name ?? '');
        setLegalName(c.legal_name ?? '');
        setRfc(c.rfc ?? '');
        setEmail(c.primary_email ?? '');
        setPhone(c.primary_phone ?? '');
        setWhatsapp(c.whatsapp ?? '');
        setRegimenFiscal(c.regimen_fiscal ?? '');
        setCfdiUse(c.cfdi_use ?? '');
        setFormaPago(c.forma_pago_default ?? '');
        setMetodoPago(c.metodo_pago_default ?? '');
        setTierId(c.tier_id);
        setType(c.customer_type);
        setStatus(c.status);
        setCreditMxn(Number(c.credit_limit_mxn ?? 0));
        setCreditDays(c.credit_days ?? 0);
      }
      if (addr) {
        setLine1(addr.line1 ?? '');
        setNeighborhood(addr.neighborhood ?? '');
        setCity(addr.city ?? '');
        setState(addr.state ?? '');
        setPostalCode(addr.postal_code ?? '');
      }
      setLoading(false);
    })();
  }, [mode, customerId]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErr(null);

    const payload = {
      business_name: businessName,
      legal_name: legalName || null,
      rfc: rfc || null,
      regimen_fiscal: regimenFiscal || null,
      cfdi_use: cfdiUse || null,
      forma_pago_default: formaPago || null,
      metodo_pago_default: metodoPago || null,
      primary_email: email || null,
      primary_phone: phone || null,
      whatsapp: whatsapp || null,
      tier_id: tierId,
      customer_type: type,
      status,
      credit_limit_mxn: creditMxn || null,
      credit_days: creditDays || null,
    };

    let customerOut: any;
    if (mode === 'create') {
      const { data, error } = await supabase.from('customers').insert(payload).select('id').single();
      if (error) { setSaving(false); setErr(error.message); return; }
      customerOut = data;
    } else {
      const { error } = await supabase.from('customers').update(payload).eq('id', customerId!);
      if (error) { setSaving(false); setErr(error.message); return; }
      customerOut = { id: customerId };
    }

    // Dirección si proveyó city/state/line1/CP
    if (city || state || line1 || postalCode) {
      if (mode === 'create') {
        await supabase.from('customer_addresses').insert({
          customer_id: customerOut.id,
          kind: 'billing',
          label: 'Principal',
          line1: line1 || '',
          neighborhood: neighborhood || null,
          city: city || null,
          state: state || null,
          postal_code: postalCode || null,
          country: 'MX',
          is_default: true,
        });
      } else {
        // upsert: borrar default existente y crear nuevo
        await supabase.from('customer_addresses').delete().eq('customer_id', customerOut.id).eq('kind', 'billing');
        await supabase.from('customer_addresses').insert({
          customer_id: customerOut.id,
          kind: 'billing',
          label: 'Principal',
          line1: line1 || '',
          neighborhood: neighborhood || null,
          city: city || null,
          state: state || null,
          postal_code: postalCode || null,
          country: 'MX',
          is_default: true,
        });
      }
    }

    setSaving(false);
    onSaved();
  }

  if (loading) {
    return (
      <Modal onClose={onClose}>
        <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>Cargando…</div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <div style={{
        fontSize: 11,
        color: theme.accentDark,
        textTransform: 'uppercase',
        letterSpacing: 1.5,
        fontWeight: 700,
      }}>
        {mode === 'create' ? 'Nuevo distribuidor' : 'Editar distribuidor'}
      </div>
      <h2 style={{
        fontFamily: theme.font.head,
        fontSize: 22,
        fontWeight: 400,
        color: theme.brand,
        margin: '4px 0 20px',
      }}>
        {mode === 'create' ? 'Alta directa' : businessName}
      </h2>

      {/* Upload CSF — disponible en alta Y en edición (re-actualizar fiscal data) */}
      <div style={{
        marginBottom: 20,
        padding: 16,
        background: theme.bgSoft,
        border: `1px dashed ${theme.accent}`,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: theme.accentDark,
          textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6,
        }}>
          🧾 {mode === 'edit' ? 'Actualizar' : 'Autocompletar'} con CSF (Constancia de Situación Fiscal)
        </div>
        <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5, marginBottom: 12 }}>
          {mode === 'edit'
            ? 'Si el cliente envió una CSF nueva (cambió razón social, domicilio o régimen), súbela aquí. Sobreescribe los campos fiscales con los datos actuales del SAT.'
            : 'Sube el PDF de la CSF del SAT y Claude extrae todos los datos automáticamente: razón social, RFC, régimen fiscal, dirección fiscal. Tú solo revisas y le das Crear.'
          }
        </div>
        <label style={{
          display: 'inline-block',
          background: csfParsing ? theme.bgSoft : theme.accent,
          color: theme.brand,
          padding: '10px 16px',
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          cursor: csfParsing ? 'wait' : 'pointer',
          border: `1px solid ${theme.accent}`,
          opacity: csfParsing ? 0.6 : 1,
        }}>
          {csfParsing ? 'Procesando PDF…' : (mode === 'edit' ? '📎 Subir CSF actualizada (PDF)' : '📎 Subir CSF (PDF)')}
          <input
            type="file"
            accept="application/pdf"
            disabled={csfParsing}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handleCsfUpload(f);
              e.target.value = ''; // reset para permitir reupload
            }}
            style={{ display: 'none' }}
          />
        </label>
        {csfErr && (
          <div style={{
            marginTop: 12, padding: 10,
            background: 'rgba(220,38,38,0.06)', border: `1px solid #B91C1C`,
            fontSize: 12, color: '#B91C1C',
          }}>
            {csfErr}
          </div>
        )}
        {csfOk && (
          <div style={{
            marginTop: 12, padding: 10,
            background: theme.accentSoft, border: `1px solid ${theme.accent}`,
            fontSize: 12, color: theme.brand,
          }}>
            {csfOk}
          </div>
        )}
      </div>

      <form onSubmit={save}>
        <Field label="Nombre de empresa *">
          <input required value={businessName} onChange={e => setBusinessName(e.target.value)} style={inputStyle} />
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>Nombre comercial / interno. NO aparece en la factura.</div>
        </Field>
        <Row>
          <Field label="Razón social (SAT)">
            <input value={legalName} onChange={e => setLegalName(e.target.value)} style={inputStyle} placeholder="Como está en el SAT" />
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>Es la que aparece en la factura (CFDI). Si la dejas vacía, se usa el nombre de empresa.</div>
          </Field>
          <Field label="RFC">
            <input value={rfc} onChange={e => setRfc(e.target.value.toUpperCase())} style={inputStyle} />
          </Field>
        </Row>
        <Field label="Régimen fiscal (catálogo SAT)">
          <select value={regimenFiscal} onChange={e => setRegimenFiscal(e.target.value)} style={inputStyle}>
            <option value="">— Seleccionar régimen —</option>
            {REGIMEN_FISCAL_SAT.map(r => (
              <option key={r.code} value={r.code}>{r.code} · {r.name}</option>
            ))}
          </select>
        </Field>
        <Row>
          <Field label="Uso CFDI default">
            <select value={cfdiUse} onChange={e => setCfdiUse(e.target.value)} style={inputStyle}>
              <option value="">— Seleccionar uso —</option>
              {USO_CFDI_SAT.map(u => (
                <option key={u.code} value={u.code}>{u.code} · {u.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Método de pago default">
            <select value={metodoPago} onChange={e => setMetodoPago(e.target.value)} style={inputStyle}>
              <option value="">— Seleccionar —</option>
              <option value="PUE">PUE · Pago en una exhibición</option>
              <option value="PPD">PPD · Pago en parcialidades / diferido</option>
            </select>
          </Field>
        </Row>
        <Field label="Forma de pago default">
          <select value={formaPago} onChange={e => setFormaPago(e.target.value)} style={inputStyle}>
            <option value="">— Seleccionar forma —</option>
            {FORMA_PAGO_SAT.map(f => (
              <option key={f.code} value={f.code}>{f.code} · {f.name}</option>
            ))}
          </select>
        </Field>
        <Row>
          <Field label="Tipo de cliente">
            <select value={type} onChange={e => setType(e.target.value)} style={inputStyle}>
              <option value="distributor">Distribuidor</option>
              <option value="electrician">Electricista</option>
              <option value="integrator">Integrador</option>
              <option value="designer">Diseñador / Especificador</option>
              <option value="end_user">Usuario final</option>
            </select>
          </Field>
          <Field label="Tier (descuento)">
            <select value={tierId ?? ''} onChange={e => setTierId(e.target.value || null)} style={inputStyle}>
              <option value="">Sin tier (PVP)</option>
              {tiers.map(t => (
                <option key={t.id} value={t.id}>
                  {t.code.toUpperCase()} · {t.name} · -{Number(t.default_discount_pct)}%
                </option>
              ))}
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Email">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Teléfono">
            <input value={phone} onChange={e => setPhone(e.target.value)} style={inputStyle} />
          </Field>
        </Row>
        <Field label="WhatsApp">
          <input value={whatsapp} onChange={e => setWhatsapp(e.target.value)} style={inputStyle} placeholder="+52 55 …" />
        </Field>
        <div style={{ margin: '14px 0 6px', fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Dirección fiscal (CFDI)</div>
        <Field label="Calle y número">
          <input value={line1} onChange={e => setLine1(e.target.value)} style={inputStyle} />
        </Field>
        <Row>
          <Field label="Colonia">
            <input value={neighborhood} onChange={e => setNeighborhood(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="C.P.">
            <input value={postalCode} onChange={e => setPostalCode(e.target.value.replace(/\D/g, '').slice(0, 5))} style={inputStyle} />
          </Field>
        </Row>
        <Row>
          <Field label="Ciudad / Municipio">
            <input value={city} onChange={e => setCity(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Estado">
            <input value={state} onChange={e => setState(e.target.value)} style={inputStyle} />
          </Field>
        </Row>
        <Row>
          <Field label="Crédito MXN">
            <input type="number" value={creditMxn} onChange={e => setCreditMxn(Number(e.target.value))} style={inputStyle} />
          </Field>
          <Field label="Días crédito">
            <input type="number" value={creditDays} onChange={e => setCreditDays(Number(e.target.value))} style={inputStyle} />
          </Field>
        </Row>
        <Field label="Status">
          <select value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
            <option value="active">Activo</option>
            <option value="lead">Aplicación / Lead</option>
            <option value="inactive">Inactivo</option>
            <option value="rejected">Rechazado</option>
          </select>
        </Field>

        {err && (
          <div style={{
            color: '#B91C1C',
            fontSize: 13,
            marginTop: 12,
            padding: 10,
            background: 'rgba(220,38,38,0.05)',
            border: `1px solid #B91C1C`,
          }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{
            background: 'transparent',
            border: `1px solid ${theme.border}`,
            color: theme.brand,
            padding: '10px 18px',
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            cursor: 'pointer',
            fontFamily: theme.font.sans,
          }}>
            Cancelar
          </button>
          <button type="submit" disabled={saving} style={{
            ...btnPrimary,
            opacity: saving ? 0.7 : 1,
            cursor: saving ? 'wait' : 'pointer',
          }}>
            {saving ? 'Guardando…' : mode === 'create' ? 'Crear' : 'Guardar cambios'}
          </button>
        </div>
      </form>
      {mode === 'edit' && customerId && (
        <ShippingAddresses customerId={customerId} fiscal={{ line1, neighborhood, city, state, postal_code: postalCode }} />
      )}
    </Modal>
  );
}

// ---------- Direcciones de envío (salen en las órdenes de entrega / remisiones) ----------
type ShipAddr = {
  id?: string; label: string; line1: string; line2: string; neighborhood: string; city: string; state: string;
  postal_code: string; reference: string; contact_name: string; contact_phone: string; delivery_notes: string; is_default: boolean;
};
const EMPTY_ADDR: ShipAddr = { label: '', line1: '', line2: '', neighborhood: '', city: '', state: '', postal_code: '', reference: '', contact_name: '', contact_phone: '', delivery_notes: '', is_default: false };

function ShippingAddresses({ customerId, fiscal }: { customerId: string; fiscal: { line1: string; neighborhood: string; city: string; state: string; postal_code: string } }) {
  const [list, setList] = useState<ShipAddr[]>([]);
  const [edit, setEdit] = useState<ShipAddr | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function load() {
    const { data } = await supabase.from('customer_addresses')
      .select('id, label, line1, line2, neighborhood, city, state, postal_code, reference, contact_name, contact_phone, delivery_notes, is_default')
      .eq('customer_id', customerId).in('kind', ['shipping', 'both'])
      .order('is_default', { ascending: false }).order('created_at');
    setList(((data as any[]) ?? []).map(a => ({ ...EMPTY_ADDR, ...Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v ?? (k === 'is_default' ? false : '')])) })) as ShipAddr[]);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  async function save() {
    if (!edit) return;
    if (!edit.line1.trim() || !edit.city.trim()) { setMsg('Captura al menos calle y número, y ciudad.'); return; }
    setBusy(true); setMsg(null);
    const makeDefault = edit.is_default || list.length === 0 || (list.length === 1 && list[0].id === edit.id);
    if (makeDefault) await supabase.from('customer_addresses').update({ is_default: false }).eq('customer_id', customerId).in('kind', ['shipping', 'both']);
    const row = {
      customer_id: customerId, kind: 'shipping', label: edit.label.trim() || 'Entrega',
      line1: edit.line1.trim(), line2: edit.line2.trim() || null, neighborhood: edit.neighborhood.trim() || null,
      city: edit.city.trim() || null, state: edit.state.trim() || null, postal_code: edit.postal_code.trim() || null, country: 'MX',
      reference: edit.reference.trim() || null, contact_name: edit.contact_name.trim() || null, contact_phone: edit.contact_phone.trim() || null,
      delivery_notes: edit.delivery_notes.trim() || null, is_default: makeDefault, updated_at: new Date().toISOString(),
    };
    const { error } = edit.id
      ? await supabase.from('customer_addresses').update(row).eq('id', edit.id)
      : await supabase.from('customer_addresses').insert(row);
    setBusy(false);
    if (error) { setMsg('Error: ' + error.message); return; }
    setEdit(null); load();
  }
  async function del(a: ShipAddr) {
    if (!a.id || !confirm(`¿Eliminar la dirección "${a.label}"? Las entregas ya hechas conservan su copia.`)) return;
    const { error } = await supabase.from('customer_addresses').delete().eq('id', a.id);
    if (error) { setMsg('Error: ' + error.message); return; }
    load();
  }
  async function makeDefault(a: ShipAddr) {
    await supabase.from('customer_addresses').update({ is_default: false }).eq('customer_id', customerId).in('kind', ['shipping', 'both']);
    await supabase.from('customer_addresses').update({ is_default: true }).eq('id', a.id!);
    load();
  }
  const F = (k: keyof ShipAddr, label: string, ph?: string, w?: number) => (
    <label style={{ display: 'block', flex: w ?? 1, minWidth: 120 }}>
      <div style={{ fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 3 }}>{label}</div>
      <input value={String(edit?.[k] ?? '')} placeholder={ph} onChange={e => setEdit(x => x ? { ...x, [k]: k === 'postal_code' ? e.target.value.replace(/\D/g, '').slice(0, 5) : e.target.value } : x)} style={inputStyle} />
    </label>
  );
  const fmt = (a: ShipAddr) => [[a.line1, a.line2].filter(Boolean).join(' '), a.neighborhood, [a.postal_code, a.city].filter(Boolean).join(' '), a.state].filter(Boolean).join(', ');

  return (
    <div style={{ marginTop: 24, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Direcciones de envío</div>
          <div style={{ fontSize: 11, color: theme.textMuted }}>Salen en las órdenes de entrega y remisiones. La predeterminada se asigna sola a los pedidos nuevos.</div>
        </div>
        {!edit && (
          <div style={{ display: 'flex', gap: 6 }}>
            {fiscal.line1 && <button type="button" onClick={() => setEdit({ ...EMPTY_ADDR, label: 'Oficina', line1: fiscal.line1, neighborhood: fiscal.neighborhood, city: fiscal.city, state: fiscal.state, postal_code: fiscal.postal_code })}
              style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '6px 10px', fontSize: 11, cursor: 'pointer', fontFamily: theme.font.sans }}>Copiar la fiscal</button>}
            <button type="button" onClick={() => setEdit({ ...EMPTY_ADDR })} style={{ ...btnPrimary, padding: '6px 12px', fontSize: 11 }}>+ Dirección</button>
          </div>
        )}
      </div>

      {list.map(a => (
        <div key={a.id} style={{ border: `1px solid ${a.is_default ? theme.accent : theme.border}`, padding: '10px 12px', marginBottom: 8, fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <div>
              <b style={{ color: theme.brand }}>{a.label || 'Entrega'}</b>
              {a.is_default && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, background: theme.accent, color: '#0e0e0e', padding: '1px 6px' }}>PREDETERMINADA</span>}
              <div style={{ color: theme.text, marginTop: 2 }}>{fmt(a)}{a.reference ? ` (Ref: ${a.reference})` : ''}</div>
              {(a.contact_name || a.contact_phone) && <div style={{ fontSize: 12, color: theme.textMuted }}>Recibe: {[a.contact_name, a.contact_phone].filter(Boolean).join(' · ')}</div>}
              {a.delivery_notes && <div style={{ fontSize: 12, color: theme.textMuted }}>Notas: {a.delivery_notes}</div>}
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', whiteSpace: 'nowrap' }}>
              {!a.is_default && <button type="button" onClick={() => makeDefault(a)} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>Predeterminar</button>}
              <button type="button" onClick={() => setEdit(a)} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>✎</button>
              <button type="button" onClick={() => del(a)} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: '#B91C1C', padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>×</button>
            </div>
          </div>
        </div>
      ))}
      {list.length === 0 && !edit && <div style={{ fontSize: 12, color: theme.textMuted, padding: '6px 0' }}>Sin direcciones de envío.</div>}

      {edit && (
        <div style={{ border: `1px solid ${theme.accent}`, padding: 12, background: theme.bgSoft }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>{F('label', 'Nombre', 'Obra Polanco, Bodega…')}{F('line1', 'Calle y número', '', 2)}{F('line2', 'Int. / piso')}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>{F('neighborhood', 'Colonia')}{F('postal_code', 'C.P.')}{F('city', 'Ciudad / Municipio')}{F('state', 'Estado')}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>{F('reference', 'Referencias', 'Entre calles, color de fachada…', 2)}{F('contact_name', 'Contacto que recibe')}{F('contact_phone', 'Teléfono')}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>{F('delivery_notes', 'Notas de entrega', 'Horario, maniobras, acceso a obra, cita previa…', 3)}</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.brand, marginBottom: 10 }}>
            <input type="checkbox" checked={edit.is_default} onChange={e => setEdit(x => x ? { ...x, is_default: e.target.checked } : x)} /> Predeterminada para pedidos nuevos
          </label>
          {msg && <div style={{ fontSize: 12, color: '#B91C1C', marginBottom: 8 }}>{msg}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => { setEdit(null); setMsg(null); }} style={{ background: 'transparent', border: `1px solid ${theme.border}`, color: theme.brand, padding: '7px 12px', fontSize: 12, cursor: 'pointer' }}>Cancelar</button>
            <button type="button" onClick={save} disabled={busy} style={{ ...btnPrimary, padding: '7px 14px', fontSize: 12 }}>{busy ? 'Guardando…' : 'Guardar dirección'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20,
    }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          padding: 32,
          width: '100%',
          maxWidth: 640,
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <button type="button" onClick={onClose} aria-label="Cerrar" title="Cerrar" style={{ position: 'absolute', top: 10, right: 14, background: 'transparent', border: 'none', color: theme.textMuted, fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 4, zIndex: 5 }}>×</button>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <div style={{
        fontSize: 11,
        color: theme.textMuted,
        marginBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        fontWeight: 700,
      }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      {children}
    </div>
  );
}

function Th({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{
      textAlign: 'left',
      padding: '12px 14px',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: theme.textMuted,
      borderBottom: `1px solid ${theme.border}`,
      ...style,
    }}>{children}</th>
  );
}

function Td({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <td style={{ padding: '10px 14px', verticalAlign: 'top', ...style }}>{children}</td>;
}

function Tag({ children, color }: { children: React.ReactNode; color: string }) {
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      padding: '2px 8px',
      color,
      border: `1px solid ${color}`,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : theme.brand,
      border: `1px solid ${active ? theme.brand : theme.border}`,
      padding: '8px 12px',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent',
      color: active ? theme.brand : theme.textMuted,
      border: 'none',
      borderBottom: `2px solid ${active ? theme.accent : 'transparent'}`,
      padding: '12px 16px',
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      marginBottom: -1,
    }}>{children}</button>
  );
}

const btnPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  border: 'none',
  padding: '10px 18px',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: theme.font.sans,
};

function btnSmall(bg: string, color: string, border?: string): React.CSSProperties {
  return {
    background: bg,
    color,
    border: border ? `1px solid ${border}` : 'none',
    padding: '6px 10px',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    cursor: 'pointer',
    fontFamily: theme.font.sans,
  };
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: theme.bg,
  border: `1px solid ${theme.border}`,
  padding: '10px 12px',
  color: theme.text,
  fontSize: 13,
  fontFamily: theme.font.sans,
  outline: 'none',
  boxSizing: 'border-box',
};

// ============================================================================
// CATÁLOGOS SAT (CFDI 4.0)
// Fuente: http://omawww.sat.gob.mx/tramitesyservicios/Paginas/catalogos_emision_cfdi_complemento_carta_porte.htm
// ============================================================================

const REGIMEN_FISCAL_SAT: { code: string; name: string }[] = [
  { code: '601', name: 'General de Ley Personas Morales' },
  { code: '603', name: 'Personas Morales con Fines no Lucrativos' },
  { code: '605', name: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { code: '606', name: 'Arrendamiento' },
  { code: '607', name: 'Régimen de Enajenación o Adquisición de Bienes' },
  { code: '608', name: 'Demás ingresos' },
  { code: '610', name: 'Residentes en el Extranjero sin Establecimiento Permanente en México' },
  { code: '611', name: 'Ingresos por Dividendos (socios y accionistas)' },
  { code: '612', name: 'Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '614', name: 'Ingresos por intereses' },
  { code: '615', name: 'Régimen de los ingresos por obtención de premios' },
  { code: '616', name: 'Sin obligaciones fiscales' },
  { code: '620', name: 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos' },
  { code: '621', name: 'Incorporación Fiscal' },
  { code: '622', name: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { code: '623', name: 'Opcional para Grupos de Sociedades' },
  { code: '624', name: 'Coordinados' },
  { code: '625', name: 'Régimen de las Actividades Empresariales con Ingresos a través de Plataformas Tecnológicas' },
  { code: '626', name: 'Régimen Simplificado de Confianza' },
];

const USO_CFDI_SAT: { code: string; name: string }[] = [
  { code: 'G01', name: 'Adquisición de mercancías' },
  { code: 'G02', name: 'Devoluciones, descuentos o bonificaciones' },
  { code: 'G03', name: 'Gastos en general' },
  { code: 'I01', name: 'Construcciones' },
  { code: 'I02', name: 'Mobiliario y equipo de oficina por inversiones' },
  { code: 'I03', name: 'Equipo de transporte' },
  { code: 'I04', name: 'Equipo de cómputo y accesorios' },
  { code: 'I05', name: 'Dados, troqueles, moldes, matrices y herramental' },
  { code: 'I06', name: 'Comunicaciones telefónicas' },
  { code: 'I07', name: 'Comunicaciones satelitales' },
  { code: 'I08', name: 'Otra maquinaria y equipo' },
  { code: 'D01', name: 'Honorarios médicos, dentales y gastos hospitalarios' },
  { code: 'D02', name: 'Gastos médicos por incapacidad o discapacidad' },
  { code: 'D03', name: 'Gastos funerales' },
  { code: 'D04', name: 'Donativos' },
  { code: 'D05', name: 'Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)' },
  { code: 'D06', name: 'Aportaciones voluntarias al SAR' },
  { code: 'D07', name: 'Primas por seguros de gastos médicos' },
  { code: 'D08', name: 'Gastos de transportación escolar obligatoria' },
  { code: 'D09', name: 'Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones' },
  { code: 'D10', name: 'Pagos por servicios educativos (colegiaturas)' },
  { code: 'S01', name: 'Sin efectos fiscales' },
  { code: 'CP01', name: 'Pagos' },
  { code: 'CN01', name: 'Nómina' },
];

const FORMA_PAGO_SAT: { code: string; name: string }[] = [
  { code: '01', name: 'Efectivo' },
  { code: '02', name: 'Cheque nominativo' },
  { code: '03', name: 'Transferencia electrónica de fondos' },
  { code: '04', name: 'Tarjeta de crédito' },
  { code: '05', name: 'Monedero electrónico' },
  { code: '06', name: 'Dinero electrónico' },
  { code: '08', name: 'Vales de despensa' },
  { code: '12', name: 'Dación en pago' },
  { code: '13', name: 'Pago por subrogación' },
  { code: '14', name: 'Pago por consignación' },
  { code: '15', name: 'Condonación' },
  { code: '17', name: 'Compensación' },
  { code: '23', name: 'Novación' },
  { code: '24', name: 'Confusión' },
  { code: '25', name: 'Remisión de deuda' },
  { code: '26', name: 'Prescripción o caducidad' },
  { code: '27', name: 'A satisfacción del acreedor' },
  { code: '28', name: 'Tarjeta de débito' },
  { code: '29', name: 'Tarjeta de servicios' },
  { code: '30', name: 'Aplicación de anticipos' },
  { code: '31', name: 'Intermediario pagos' },
  { code: '99', name: 'Por definir' },
];
